import * as Actions from '@va/standalone/shared/actions';
import * as Types from '@va/standalone/shared/actions';
import { setWebsiteCreationStep } from '@va/standalone/shared/actions';
import {
  dataViewerHistoryRoutes,
  dataViewerRoutesNoHistory,
  modalName,
  storageItems,
  SUBSCRIPTION_PROVIDER_TYPE,
} from '@va/standalone/shared/constants';
import {
  addCredentialsToLocalStorage,
  getBaseRouteFromHistoryRoute,
  getLastWebsiteId,
  getRouteWithoutWebsiteIdPrefix,
  handleExpiredRefreshToken,
  handleLastSelectedWebsite,
  isAccountSettingsPageSelected,
  isUserLoggedInWithCustomUrl,
} from '@va/standalone/shared/helpers';
import * as Selectors from '@va/standalone/shared/selectors';
import { getPlanDetailsById, userHasFinishedOnboarding } from '@va/standalone/shared/selectors';
import { Platform, UserRole } from '@va/types/website';
import { LocalStorage, setDefaultTheme } from '@va/util/helpers';
import moment from 'moment';

import {
  activeWebsiteSuccessullyInitialized,
  HANDLE_ANNOUNCEMENT_MODAL,
  handleAnnouncementModal,
  set,
  setInstanceId,
  setWebsiteProperty,
  switchActiveWebsite,
} from '@va/dashboard/actions/app';

import {
  ADD_NOTIFICATION_EVENT,
  bannerName,
  modalName as commonModalName,
  DEFAULT_NON_AWL_SUPPORT_CENTER_URL,
  defaultTrafficFilter,
  defaultWebsiteId,
  lastDateModalShown,
  notificationTransKeys,
  pathName,
  platform,
  TRAFFIC_FILTER,
  websiteCreationSteps,
  websiteFlagKeys,
} from '@va/constants';
import { EventBus } from '@va/util/misc';
import { all, call, put, select, spawn, take, takeLatest } from 'redux-saga/effects';

import {
  clearNotifications,
  CLOSE_MODAL,
  closeModal,
  hideBanner,
  openCheckLoginModal,
  openModal,
  setActiveTab,
  setSupportCenterDomain,
  setTrafficFilter,
  showBanner,
} from '@va/dashboard/actions/ui';
import {
  checkSsrSettings,
  fetchAndPopulateExtraWebsiteInfoSaga,
  fetchVisitLimitReachedAtSaga,
  modulePermissionsSaga,
  setLimitNotificationShown,
  showLimitNotification,
} from '@va/dashboard/sagas/app';
import { getApiRequest } from '@va/dashboard/selectors/api';
import { getActiveTab, getTrafficFilter } from '@va/dashboard/selectors/ui';
import { appHistory } from '@va/shared/router';
import { refreshToken } from '../../apiClient';

import {
  GET_FEATURE_PERMISSIONS_FAILED,
  GET_FEATURE_PERMISSIONS_SUCCEEDED,
  getDynamicPages,
  getFeaturePermissions,
  getPrivacySettings,
  getSsrSettings,
  loadExternalDashboardScript,
  markModalAsViewed,
  resetRequest,
  resetRequestStatus,
} from '@va/dashboard/actions/api';
import { getWebsiteSaga } from '@va/dashboard/sagas/api';
import { getInstanceId, isTrackingCodeInstalled } from '@va/dashboard/selectors/app';
import {
  getWebsite,
  getWebsitePlatform,
  selectWebsiteMonthlyVisits,
  websiteRoleSelectors,
} from '@va/dashboard/selectors/core';
import { getProviderName } from '@va/dashboard/selectors/customization';
import { activeWebsiteSlice } from '@va/dashboard/slices';
import dayjs from 'dayjs';
import { handleAnnouncementModalSaga, handleShortBusinessInfoModal } from './modals';

export function* watchers() {
  yield all([
    takeLatest(Types.standalone.App.START_CANCEL_SUBSCRIPTION_FLOW, cancelSubscriptionFlow),
    takeLatest(Types.standalone.App.START_CHANGE_EMAIL_FLOW, changeEmailFlow),
    takeLatest(Types.standalone.App.START_DELETE_ACCOUNT_FLOW, deleteAccountFlow),

    takeLatest(switchActiveWebsite, switchActiveWebsiteSaga),

    takeLatest(Types.standalone.App.START_LEAVE_WEBSITE_FLOW, leaveWebsiteFlow),
    takeLatest(Types.standalone.App.START_CONFIRM_CONTRIBUTOR_FLOW, confirmContributorFlow),
    takeLatest(Types.standalone.App.START_CHECK_PASSWORD_FLOW, checkPasswordFlow),
    takeLatest(HANDLE_ANNOUNCEMENT_MODAL, handleAnnouncementModalSaga),
    takeLatest(Types.standalone.App.AUTH_EXTERNAL, handleAuthExternal),
    takeLatest(Types.standalone.App.REFRESH_SUBSCRIPTION_DETAILS, refreshSubscriptionDetails),
  ]);
}

function* cancelSubscriptionFlow() {
  yield put(openCheckLoginModal(modalName.CANCEL_SUBSCRIPTION, 'theme-red'));

  const checkLogin = yield take([Types.standalone.Api.CHECK_LOGIN_SUCCEEDED, CLOSE_MODAL]);
  if (checkLogin.type === Types.standalone.Api.CHECK_LOGIN_SUCCEEDED) {
    yield put(resetRequestStatus('checkLogin'));
    yield put(closeModal(modalName.CHECK_LOGIN));
    yield put(openModal(modalName.CANCEL_SUBSCRIPTION));
  } else if (checkLogin.type === CLOSE_MODAL) {
    yield put(resetRequestStatus('checkLogin'));
  }
}

export function* changeEmailFlow() {
  yield put(openCheckLoginModal('changeEmail'));

  //No need to wait for check login failed, because error is handled in the modal
  // and the modal need to stay open even if check login fails.
  const checkLogin = yield take([Types.standalone.Api.CHECK_LOGIN_SUCCEEDED, CLOSE_MODAL]);
  if (checkLogin.type === Types.standalone.Api.CHECK_LOGIN_SUCCEEDED) {
    yield put(resetRequestStatus('checkLogin'));
    yield put(closeModal('checkLogin'));
    yield put(openModal('changeEmail'));
  } else if (checkLogin.type === CLOSE_MODAL) {
    yield put(resetRequestStatus('checkLogin'));
  }
}

export function* deleteAccountFlow() {
  const userHasWebsites = yield select(Selectors.standalone.Api.userHasWebsites);
  const providerName = yield select(getProviderName);
  if (userHasWebsites && !isUserLoggedInWithCustomUrl()) {
    yield put(openModal('deleteAccount'));
  } else {
    yield put(openCheckLoginModal('deleteAccount', 'theme-red'));

    const checkLogin = yield take([Types.standalone.Api.CHECK_LOGIN_SUCCEEDED, CLOSE_MODAL]);
    if (checkLogin.type === Types.standalone.Api.CHECK_LOGIN_SUCCEEDED) {
      yield put(resetRequestStatus('checkLogin'));
      yield put(closeModal('checkLogin'));
      yield put(Actions.standalone.Api.deleteAccount());

      EventBus.dispatch(ADD_NOTIFICATION_EVENT, {
        translationKey: 'modal.deleteAccount.emailRequest.succeeded',
        translationKeyParams: { providerName },
        type: 'success',
      });
    } else if (checkLogin.type === CLOSE_MODAL) {
      yield put(resetRequestStatus('checkLogin'));
    }
  }
}

function* resetPreviousRequestsData() {
  const requests = ['createPaymentMethod', 'getDynamicPages', 'getConversionTypes', 'getFeaturePermissions'];
  for (let i = 0; i < requests.length; i++) {
    yield put(resetRequest(requests[i]));
  }
}

export function* switchActiveWebsiteSaga(action) {
  yield put(activeWebsiteSlice.actions.setIsLoading(true));

  const payload = action.payload;

  try {
    const oldWebsiteId = yield select(getInstanceId);
    //reset siteurl
    yield put(
      set({
        siteUrl: '#',
      }),
    );

    //clear notification
    yield put(clearNotifications());
    yield put(hideBanner(bannerName.LIMIT_REACHED));
    yield spawn(resetPreviousRequestsData);

    // TODO Dumi Recheck those redirects
    if (payload.handleRoute) {
      const processedRoute = getRouteWithoutWebsiteIdPrefix(appHistory.location.pathname);
      if (dataViewerRoutesNoHistory.includes(processedRoute)) {
        // do nothing - should be rechecked
      } else if (dataViewerHistoryRoutes.includes(processedRoute)) {
        //Redirect to previous tab on history routes.
        const baseTabFromHistory = getBaseRouteFromHistoryRoute(processedRoute);

        if (baseTabFromHistory) {
          appHistory.push(baseTabFromHistory);
        }
      } else {
        //Redirect to overview tab for non data-viewer tabs.
        appHistory.push('/');
      }
    }

    const websiteId = payload.websiteId;

    if (appHistory.location.pathname.includes('/website/')) {
      const pathNameArr = appHistory.location.pathname.split('/');
      pathNameArr[2] = websiteId; // pathNameArr[2] represents oldWebsiteId from url
      const queryParams = appHistory.location.search ?? '';
      const newLocationPathname = pathNameArr.join('/') + queryParams + appHistory.location.hash;
      // ensure a new entry is added to the history only if the website id changes
      if (oldWebsiteId !== defaultWebsiteId && oldWebsiteId !== websiteId) {
        appHistory.push(newLocationPathname, {}, true);
      } else {
        appHistory.replace(newLocationPathname, {}, true);
      }
    }

    handleLastSelectedWebsite(websiteId);

    yield put(setInstanceId(websiteId));

    const websiteCall = call(getWebsiteSaga);
    const extraWebsiteInfoCall = call(fetchAndPopulateExtraWebsiteInfoSaga, {
      websiteId: websiteId,
      userId: localStorage.getItem(storageItems.userId) ?? '',
    });

    const [website] = yield all([websiteCall, extraWebsiteInfoCall]);

    yield spawn(fetchVisitLimitReachedAtSaga, { websiteId });

    moment.tz.setDefault(website.timezone);
    dayjs.tz.setDefault(website.timezone);
    window.timezone = website.timezone;

    const role = yield select(websiteRoleSelectors.selectWebsiteRole);

    yield call(modulePermissionsSaga, role);

    if (role !== UserRole.DASHBOARD) {
      yield put(getPrivacySettings());
      yield put(getSsrSettings());
      yield spawn(checkSsrSettings);
    }

    const creationCompletedFlag =
      website.frontendFlags && website.frontendFlags[websiteFlagKeys.CREATION_PROCESS_COMPLETED];

    if (creationCompletedFlag === false && !isAccountSettingsPageSelected()) {
      yield put(setWebsiteCreationStep(websiteCreationSteps.privacyLevels));
      appHistory.push('/my-account/add-website');
    }

    if (!isAccountSettingsPageSelected() || payload.fromManageWebsites) {
      if (website.whiteLabel) {
        yield put(Actions.standalone.Api.getAgencyUi());
      } else {
        yield put(resetRequest('getAgencyUi'));
        yield put(setSupportCenterDomain(DEFAULT_NON_AWL_SUPPORT_CENTER_URL));
        setDefaultTheme();
      }
    }

    yield put(getFeaturePermissions());

    if (role !== UserRole.DASHBOARD) {
      yield spawn(handleTrackingCodeRedirectSaga);
    }

    // Always wait for the freshly-requested feature permissions to settle before finishing the switch to correctly set the datepicker interval.
    const getPermissionsAction = yield take([GET_FEATURE_PERMISSIONS_SUCCEEDED, GET_FEATURE_PERMISSIONS_FAILED]);
    if (!isAccountSettingsPageSelected() && getPermissionsAction.type === GET_FEATURE_PERMISSIONS_SUCCEEDED) {
      yield spawn(handleLimitNotifications);
    }
    //Force re-requesting data on currently data-viewer tab.
    if (payload.fromManageWebsites) {
      appHistory.push('/');
    } else {
      if (defaultWebsiteId !== oldWebsiteId) {
        const activeTab = yield select(getActiveTab);
        if (activeTab) {
          yield put(setActiveTab(activeTab));
        }
      }
    }

    if (role !== UserRole.DASHBOARD) {
      if (website.platform !== platform.WIX) {
        yield spawn(handleSubscriptionDetails);
      }
      yield put(getDynamicPages());
      yield put(Actions.standalone.Api.getPaymentPlans());
      yield put(handleAnnouncementModal());
      yield spawn(handleShortBusinessInfoModal);
    }
    //todo temporary fix
    //mark next selected website as having first visit
    yield put(setWebsiteProperty('hasFirstVisit', true));

    if (appHistory.location.pathname === `/website/${websiteId}/upgrade` && role !== UserRole.OWNER) {
      appHistory.push('/');
    }

    yield put(
      set({
        isPublish: 'true',
        siteUrl: website.url,
      }),
    );

    yield spawn(handleTrafficFilter);
    yield put(loadExternalDashboardScript());

    yield put(activeWebsiteSuccessullyInitialized());
  } catch (error) {
    if (error instanceof Error) {
      yield put(activeWebsiteSlice.actions.setError(error.message));
    }
    console.error(error);
  } finally {
    yield put(activeWebsiteSlice.actions.setIsLoading(false));
  }
}

function* handleTrackingCodeRedirectSaga() {
  const websiteId = yield select(getInstanceId);
  const trackingCodeInstalled = yield select(isTrackingCodeInstalled);
  const visitCount = yield select(selectWebsiteMonthlyVisits);
  const hasFinishedOnboarding = yield select(userHasFinishedOnboarding);
  const websitePlatform = yield select(getWebsitePlatform);

  // We rely on WiX for adding the tracking code. WiX users can add the SSR code, but it is not mandatory
  if (websitePlatform === Platform.WIX) return;

  if (visitCount !== 0) return;
  if (trackingCodeInstalled) return;
  if (isAccountSettingsPageSelected()) return;
  if (window.location.pathname.includes('/invoice')) return;
  if (window.location.pathname.includes('/upgrade')) return;
  if (!hasFinishedOnboarding) return;
  if (window.skipTrackingCodeRedirect) {
    window.skipTrackingCodeRedirect = false;
    return;
  }

  appHistory.push(`/website/${websiteId}/settings/tracking-code`);
}

function* handleSubscriptionDetails() {
  yield put(resetRequest('getAllSubscriptions'));
  yield put(resetRequest('getPaymentMethod'));
  yield put(resetRequest('getUpdatePaymentMethodUrl'));
  yield put(resetRequest('updateSubscription'));
  yield put(Actions.standalone.Api.getAllSubscriptions());
  yield take([Types.standalone.Api.GET_ALL_SUBSCRIPTIONS_SUCCEEDED, Types.standalone.Api.GET_ALL_SUBSCRIPTIONS_FAILED]);
  const lastCreatedSubscription = yield select(Selectors.standalone.App.getLastCreatedSubscription);
  const website = yield select(getWebsite);
  if (website?.hasCustomer) {
    yield put(Actions.standalone.Api.getCustomer());
  } else {
    yield put(Actions.standalone.Api.getCustomerFailed());
  }
  const getCustomerAction = yield take([
    Types.standalone.Api.GET_CUSTOMER_SUCCEEDED,
    Types.standalone.Api.GET_CUSTOMER_FAILED,
  ]);
  if (lastCreatedSubscription) {
    if (lastCreatedSubscription.type === SUBSCRIPTION_PROVIDER_TYPE.BRAINTREE) {
      if (getCustomerAction.type === Types.standalone.Api.GET_CUSTOMER_SUCCEEDED) {
        yield put(Actions.standalone.Api.getPaymentMethod());
      }
      yield put(Actions.standalone.Api.getPaymentAuthToken());
    } else {
      yield put(Actions.standalone.Api.getPaymentMethod());
    }
  }
}

function* handleTrafficFilter() {
  const website = yield select(getWebsite);
  const trafficFilter = yield select(getTrafficFilter);
  if (trafficFilter.period === TRAFFIC_FILTER.SPECIFIC_YEAR) {
    if (trafficFilter.specificYear < moment(website.installDate * 1000).year()) {
      yield put(setTrafficFilter(defaultTrafficFilter));
    }
  }
}

function* handleLimitNotifications() {
  const website = yield select(getWebsite);
  const isSumoUser = yield select(Selectors.standalone.App.isSumoUser);
  const websiteId = yield select(getInstanceId);
  const lastSubscription = yield select(Selectors.standalone.App.getLastCreatedSubscription);
  const numberOfMonthlyVisits = yield select(selectWebsiteMonthlyVisits);
  const featurePermissions = yield select(getApiRequest, 'getFeaturePermissions');
  const limitFromPermissions = featurePermissions && featurePermissions.visits && featurePermissions.visits.limit;
  const limitFromSubscription = yield select((state) => getPlanDetailsById(state, lastSubscription.planId)?.visits);
  const sumoLimit = isSumoUser ? website.sumoLimit : null;
  const limit = limitFromPermissions || limitFromSubscription || sumoLimit;

  const markVisitorCurrentDate = moment().format('YYYY-MM');
  const modalShowed =
    website.frontendFlags &&
    website.frontendFlags[commonModalName.visitsLimitReached + `-${lastDateModalShown}`] === markVisitorCurrentDate;

  if (limit && numberOfMonthlyVisits >= (50 * limit) / 100) {
    if (numberOfMonthlyVisits >= limit) {
      yield put(showBanner(bannerName.LIMIT_REACHED));
      modalShowed
        ? yield spawn(showLimitNotification, 100, 'warning', true, notificationTransKeys.visitsLimitReachedUpgrade)
        : yield put(openModal(commonModalName.visitsLimitReached));
    } else if (numberOfMonthlyVisits >= (90 * limit) / 100) {
      yield spawn(showLimitNotification, 90, 'warning', true);
      setLimitNotificationShown(90, websiteId);
    } else if (numberOfMonthlyVisits >= (75 * limit) / 100) {
      yield spawn(showLimitNotification, 75, 'warning', true);
      setLimitNotificationShown(75, websiteId);
    } else if (numberOfMonthlyVisits >= (50 * limit) / 100) {
      yield spawn(showLimitNotification, 50, 'warning', true);
      setLimitNotificationShown(50, websiteId);
    }
  }
}

export function* resetVisitsLimitNotificationMonthlyFlag() {
  try {
    const website = yield select(getWebsite);
    const markVisitorCurrentDate = moment().format('YYYY-MM');
    const visitsLimitReachedModalShowedInCurrentMonth =
      website.frontendFlags &&
      website.frontendFlags[modalName.visitsLimitReached + `-${lastDateModalShown}`] === markVisitorCurrentDate;
    if (visitsLimitReachedModalShowedInCurrentMonth) {
      yield put(markModalAsViewed(modalName.visitsLimitReached + `-${lastDateModalShown}`, false));
    }
  } catch (e) {
    //
  }
}

function* leaveWebsiteFlow(action) {
  yield put(Actions.standalone.App.startCheckPasswordFlow(modalName.LEAVE_WEBSITE, 'theme-red'));

  const checkLogin = yield take(Types.standalone.App.END_CHECK_PASSWORD_FLOW);

  if (checkLogin.succeeded) {
    yield put(openModal(modalName.LEAVE_WEBSITE));

    yield put(Actions.standalone.Api.leaveWebsite(action.websiteId));
  }
}

function* confirmContributorFlow() {
  const confirmContributor = yield take([Types.standalone.Api.ACCEPT_CONTRIBUTOR_ROLE_REQUEST, CLOSE_MODAL]);

  if (
    confirmContributor.type === Types.standalone.Api.ACCEPT_CONTRIBUTOR_ROLE_REQUEST ||
    confirmContributor.type === CLOSE_MODAL
  ) {
    //Remove pending confirmation info form localStorage.
    LocalStorage.removeItem(storageItems.pendingConfirmation);
    //Remove query string params
    appHistory.replace({ pathname: '/', search: '' });
  }
}

function* checkPasswordFlow(action) {
  yield put(openCheckLoginModal(action.nextModal, action.theme));

  const checkLogin = yield take([Types.standalone.Api.CHECK_LOGIN_SUCCEEDED, CLOSE_MODAL]);

  const succeeded = checkLogin.type === Types.standalone.Api.CHECK_LOGIN_SUCCEEDED;
  if (succeeded) {
    yield put(closeModal(modalName.CHECK_LOGIN));
  }

  yield put(resetRequestStatus(modalName.CHECK_LOGIN));
  yield put(Actions.standalone.App.endCheckPasswordFlow(succeeded));
}

function* handleAuthExternal(action) {
  try {
    yield put(Actions.standalone.Api.refreshToken());
    const data = yield call(refreshToken, { refreshToken: action.token });
    addCredentialsToLocalStorage(data);
    window.refreshTokenPromise = null;
    yield put(Actions.standalone.Api.loginSucceeded(data));
    yield put(Actions.standalone.Api.refreshTokenEnded());

    yield put(Actions.standalone.App.initialize());
    //Wait for Initialize process to end in order to know where to redirect.
    yield take(Types.standalone.App.INITIALIZE_SUCCEEDED);
    const defaultRedirectRoute = getLastWebsiteId() ? '/' : pathName.manageWebsites;

    appHistory.push(defaultRedirectRoute);
  } catch (error) {
    window.refreshTokenPromise = null;
    handleExpiredRefreshToken(appHistory.location.pathname);
    yield put(Actions.standalone.Api.refreshTokenEnded());
    appHistory.push('/login');
  }
}

function* refreshSubscriptionDetails() {
  try {
    yield put(getFeaturePermissions());
    yield put(Actions.standalone.Api.getAllSubscriptions());
    const getSubscriptionRequest = yield take([
      Types.standalone.Api.GET_ALL_SUBSCRIPTIONS_SUCCEEDED,
      Types.standalone.Api.GET_ALL_SUBSCRIPTIONS_FAILED,
    ]);
    if (getSubscriptionRequest.type === Types.standalone.Api.GET_ALL_SUBSCRIPTIONS_SUCCEEDED) {
      yield put(Actions.standalone.Api.getPaymentMethod());
    }
  } catch (error) {
    console.error(error);
  }
}
